import React, { useState } from 'react';
import { KLINIO_DEFAULT_ACCENT } from './BrandContext';
import { isCampaignActive, currencyLabel } from './clientUtils';
import { NEUTRAL, STATUS_COLORS, resolveTheme, onAccent } from './designTokens';
import { DAY_NAMES, MONTHS, initials } from './BookingDayBand';

// T-16/T-14: "Boka tid" i ny stil. Steg i samma ruta: kund (sök/lista eller ny kund) →
// behandling (förvald: kundens senaste) → anteckning → Boka. Krock (23P01) visar
// närliggande lediga tider; kund och behandling ligger kvar.

const fmtDate = (ds) => {
  const d = new Date(`${ds}T12:00:00`);
  return `${DAY_NAMES[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

function BookingClientPickerModal({
  pendingSlot,
  bookingSuccessInfo,
  accentColor = KLINIO_DEFAULT_ACCENT,
  closeClientPicker,
  services,
  pickerServiceId,
  setPickerServiceId,
  bookingNotes,
  setBookingNotes,
  clientSearch,
  setClientSearch,
  filteredForBooking,
  bookingLoading,
  confirmBooking,
  showNewClientForm,
  setShowNewClientForm,
  newClientName,
  setNewClientName,
  newClientEmail,
  setNewClientEmail,
  newClientPhone,
  setNewClientPhone,
  addClientAndBook,
  pickerClient,
  selectPickerClient,
  conflict,
  pickAlternative,
}) {
  const theme = resolveTheme(accentColor);
  const [hoverId, setHoverId] = useState(null);
  if (!pendingSlot) return null;

  const active = services.filter(s => s.is_active);
  const needsService = active.length > 0;
  const chosen = active.find(s => s.id === pickerServiceId) || null;
  const priceOf = (s) => (isCampaignActive(s) ? s.campaign_price : s.price);
  const canBook = !bookingLoading && (!needsService || !!chosen) && (pickerClient || (showNewClientForm && newClientName.trim()));
  const when = `${fmtDate(pendingSlot.dateStr)} · ${pendingSlot.slotStart}–${pendingSlot.slotEnd}`;

  const btn = { padding: '10px 16px', minHeight: '42px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };
  const primary = { ...btn, background: theme.accent, color: onAccent(theme.accent), border: `1px solid ${theme.accent}`, fontWeight: '700' };
  const ghost = { ...btn, background: '#FFFFFF', color: NEUTRAL.ink, border: '1px solid #D5DBE4' };
  const field = { fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: '1px solid #D5DBE4', fontSize: '14px', color: NEUTRAL.ink, outline: 'none' };
  const label = { fontSize: '13px', fontWeight: '600', color: NEUTRAL.ink };
  const avatar = (name, size = 36) => (
    <span style={{ width: `${size}px`, height: `${size}px`, borderRadius: '999px', background: theme.accent_soft_bg, color: theme.accent_soft_text, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: '800', flexShrink: 0 }}>{initials(name)}</span>
  );

  const submit = (e) => {
    if (e) e.preventDefault();
    if (!canBook) return;
    if (pickerClient) confirmBooking(pickerClient);
    else addClientAndBook(e || { preventDefault() {} });
  };

  return (
    <div className="fr-picker-backdrop" style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div
        className="fr-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="picker-title"
        style={{ background: '#FFFFFF', borderRadius: '14px', boxShadow: '0 12px 40px rgba(0,0,0,0.18)', maxWidth: '560px', width: '100%', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {bookingSuccessInfo ? (
          <div style={{ textAlign: 'center', padding: '32px 24px 24px' }}>
            <div style={{ width: '52px', height: '52px', background: STATUS_COLORS.success_bg, borderRadius: '50%', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <h2 id="picker-title" style={{ margin: '0 0 6px', color: NEUTRAL.ink, fontSize: '18px', fontWeight: '800', fontFamily: 'var(--font-heading)' }}>Bokad</h2>
            <p style={{ margin: '0 0 4px', color: NEUTRAL.ink, fontWeight: '600', fontSize: '15px' }}>{bookingSuccessInfo.clientName}</p>
            <p style={{ margin: '0 0 24px', color: NEUTRAL.muted, fontSize: '14px' }}>
              {fmtDate(bookingSuccessInfo.date)} · {bookingSuccessInfo.startTime}–{bookingSuccessInfo.endTime}
            </p>
            <button type="button" onClick={closeClientPicker} style={{ ...primary, padding: '10px 28px' }}>Klar</button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div className="fr-picker-grab" aria-hidden="true" style={{ display: 'none', justifyContent: 'center', paddingTop: '8px' }}><span style={{ width: '40px', height: '4px', borderRadius: '999px', background: '#D5DBE4' }} /></div>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', padding: '18px 22px', borderBottom: `1px solid ${theme.card_border}` }}>
              <div>
                <h2 id="picker-title" style={{ margin: 0, fontSize: '18px', fontWeight: '800', fontFamily: 'var(--font-heading)', color: NEUTRAL.ink }}>Boka tid</h2>
                <div style={{ fontSize: '13px', color: NEUTRAL.muted, marginTop: '2px' }}>{when}</div>
              </div>
              <button type="button" aria-label="Stäng" onClick={closeClientPicker} style={{ background: NEUTRAL.page, border: 'none', color: NEUTRAL.text, width: '40px', height: '40px', borderRadius: '999px', fontSize: '18px', cursor: 'pointer', flexShrink: 0 }}>×</button>
            </div>

            <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: '18px', overflowY: 'auto' }}>
              {conflict && (
                <div role="alert" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ borderRadius: '10px', background: STATUS_COLORS.warning_bg, padding: '10px 12px', fontSize: '13px', color: '#92400E' }}>
                    <strong>Tiden hann bli bokad.</strong> {fmtDate(conflict.dateStr)} {conflict.start} är inte längre ledig. Välj en annan tid – kund och behandling ligger kvar.
                  </div>
                  {conflict.alternatives.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {conflict.alternatives.map(a => {
                        const d = new Date(`${a.dateStr}T12:00:00`);
                        return (
                          <button key={`${a.dateStr}-${a.start}`} type="button" onClick={() => pickAlternative(a)} style={{ ...ghost, borderRadius: '999px', minHeight: '40px', padding: '8px 14px' }}>
                            {DAY_NAMES[d.getDay()].slice(0, 3)} {d.getDate()} {MONTHS[d.getMonth()].slice(0, 3)} {a.start}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ fontSize: '13px', color: NEUTRAL.muted }}>Inga lediga tider de närmaste två veckorna. Stäng och välj i kalendern.</div>
                  )}
                </div>
              )}

              {/* Kund */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={label}>Kund</div>
                {pickerClient ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', border: `1px solid ${theme.card_border}`, borderRadius: '10px', padding: '10px 12px' }}>
                    {avatar(pickerClient.name)}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: NEUTRAL.ink }}>{pickerClient.name}</div>
                      <div style={{ fontSize: '12px', color: NEUTRAL.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[pickerClient.email, pickerClient.phone].filter(Boolean).join(' · ')}</div>
                    </div>
                    <button type="button" onClick={() => selectPickerClient(null)} style={{ ...ghost, minHeight: '36px', padding: '7px 12px', fontSize: '12px' }}>Byt</button>
                  </div>
                ) : showNewClientForm ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', border: `1px solid ${theme.card_border}`, borderRadius: '10px', padding: '12px' }}>
                    <label style={{ ...label, fontWeight: '500', fontSize: '12px', color: NEUTRAL.muted }} htmlFor="nc-name">Namn *</label>
                    <input id="nc-name" type="text" value={newClientName} onChange={e => setNewClientName(e.target.value)} required autoFocus style={field} />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }} className="fr-picker-newclient">
                      <div>
                        <label style={{ ...label, fontWeight: '500', fontSize: '12px', color: NEUTRAL.muted }} htmlFor="nc-email">E-post</label>
                        <input id="nc-email" type="email" value={newClientEmail} onChange={e => setNewClientEmail(e.target.value)} style={{ ...field, marginTop: '4px' }} />
                      </div>
                      <div>
                        <label style={{ ...label, fontWeight: '500', fontSize: '12px', color: NEUTRAL.muted }} htmlFor="nc-phone">Telefon</label>
                        <input id="nc-phone" type="tel" value={newClientPhone} onChange={e => setNewClientPhone(e.target.value)} style={{ ...field, marginTop: '4px' }} />
                      </div>
                    </div>
                    <button type="button" onClick={() => setShowNewClientForm(false)} style={{ ...ghost, alignSelf: 'flex-start', minHeight: '36px', padding: '7px 12px', fontSize: '12px' }}>Välj befintlig kund i stället</button>
                  </div>
                ) : (
                  <div style={{ border: `1px solid ${theme.card_border}`, borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ padding: '10px' }}>
                      <input type="search" aria-label="Sök kund" placeholder="Sök på namn, e-post eller telefon" value={clientSearch} onChange={e => setClientSearch(e.target.value)} autoFocus style={{ ...field, height: '44px' }} />
                    </div>
                    <button type="button" onClick={() => setShowNewClientForm(true)} style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', background: 'transparent', border: 'none', borderTop: `1px solid ${theme.card_border}`, padding: '0 14px', height: '52px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}>
                      <span aria-hidden="true" style={{ width: '34px', height: '34px', borderRadius: '999px', border: `1.5px dashed ${theme.accent_soft_text}`, color: theme.accent_soft_text, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '600' }}>+</span>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: theme.accent_soft_text }}>Ny kund</span>
                    </button>
                    <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                      {filteredForBooking.length === 0 ? (
                        <div style={{ padding: '14px', fontSize: '13px', color: NEUTRAL.muted, borderTop: `1px solid ${theme.card_border}` }}>Inga kunder matchar sökningen.</div>
                      ) : filteredForBooking.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => selectPickerClient(c)}
                          onMouseEnter={() => setHoverId(c.id)}
                          onMouseLeave={() => setHoverId(null)}
                          style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', background: hoverId === c.id ? NEUTRAL.page : '#FFFFFF', border: 'none', borderTop: `1px solid ${theme.card_border}`, padding: '0 14px', minHeight: '58px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}
                        >
                          {avatar(c.name, 34)}
                          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: NEUTRAL.ink }}>{c.name}</span>
                            <span style={{ fontSize: '12px', color: NEUTRAL.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[c.email, c.phone].filter(Boolean).join(' · ')}</span>
                          </span>
                          <span aria-hidden="true" style={{ color: NEUTRAL.faint, fontSize: '16px' }}>›</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Behandling */}
              {needsService && (
                <div role="radiogroup" aria-label="Behandling" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={label}>Behandling</div>
                  {active.map(s => {
                    const on = s.id === pickerServiceId;
                    const price = priceOf(s);
                    return (
                      <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', borderRadius: '10px', padding: '0 14px', minHeight: '46px', cursor: 'pointer', border: on ? `1.5px solid ${theme.accent}` : `1px solid ${theme.card_border}`, background: on ? theme.accent_soft_bg : '#FFFFFF' }}>
                        <input type="radio" name="picker-service" checked={on} onChange={() => setPickerServiceId(s.id)} style={{ accentColor: theme.accent, width: '16px', height: '16px', margin: 0 }} />
                        <span style={{ flex: 1, minWidth: 0, fontSize: '14px', fontWeight: '600', color: NEUTRAL.ink }}>
                          {s.title}{isCampaignActive(s) && s.campaign_label ? ` · ${s.campaign_label}` : ''}
                        </span>
                        {pickerClient && s.id === pickerClient.lastServiceId && <span style={{ background: theme.accent_soft_bg, color: theme.accent_soft_text, padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '600', border: on ? `1px solid ${theme.accent_soft_text}33` : 'none' }}>Senast</span>}
                        <span style={{ fontSize: '13px', color: NEUTRAL.muted, whiteSpace: 'nowrap' }}>{s.duration} min{price != null ? ` · ${price} ${currencyLabel(s.currency)}` : ''}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Anteckning */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label htmlFor="picker-note" style={label}>Anteckning <span style={{ fontWeight: '400', color: NEUTRAL.muted }}>· valfritt</span></label>
                <textarea id="picker-note" rows={2} placeholder="T.ex. önskemål inför besöket" value={bookingNotes} onChange={e => setBookingNotes(e.target.value)} style={{ ...field, fontSize: '13px', resize: 'vertical' }} />
              </div>
            </div>

            <div className="fr-picker-foot" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '14px 22px', borderTop: `1px solid ${theme.card_border}`, background: '#FBFCFD' }}>
              <div style={{ fontSize: '13px', color: NEUTRAL.text, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {[pickerClient?.name || (showNewClientForm ? newClientName.trim() : null), chosen?.title].filter(Boolean).join(' · ')}
                {chosen && priceOf(chosen) != null && <> · <strong style={{ color: NEUTRAL.ink }}>{priceOf(chosen)} {currencyLabel(chosen.currency)}</strong></>}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button type="button" onClick={closeClientPicker} style={ghost}>Avbryt</button>
                <button type="submit" disabled={!canBook} style={{ ...primary, opacity: canBook ? 1 : 0.5, cursor: canBook ? 'pointer' : 'default' }}>
                  {bookingLoading ? 'Bokar…' : showNewClientForm && !pickerClient ? 'Lägg till och boka' : 'Boka'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default BookingClientPickerModal;
